(ns app.domain.address.model
  (:require #?(:clj  [core.i18n.core :as i18n])
            [clojure.string :as str]
            [core.util.schemas :as schemas]
            [schema-tools.core :as st]
            [schema.core :as s]))


(def common-street-types
  [:street-type/avenue :street-type/boulevard :street-type/concession :street-type/court :street-type/crescent
   :street-type/highway :street-type/lane :street-type/line :street-type/road :street-type/street
   :street-type/townline :street-type/way :street-type/strasse])


(def other-street-types
  [:street-type/abbey :street-type/acres :street-type/alley :street-type/bay :street-type/beach
   :street-type/bend :street-type/by-pass :street-type/byway :street-type/campus
   :street-type/cape :street-type/centre :street-type/chase :street-type/circle :street-type/circuit :street-type/close
   :street-type/common :street-type/concession :street-type/corners :street-type/cove
   :street-type/crossing :street-type/cul-de-sac :street-type/dale :street-type/dell
   :street-type/diversion :street-type/downs :street-type/drive :street-type/end :street-type/esplanade
   :street-type/estates :street-type/expressway :street-type/extension :street-type/farm :street-type/field
   :street-type/forest :street-type/freeway :street-type/front :street-type/gardens :street-type/gate :street-type/glade
   :street-type/glen :street-type/green :street-type/grounds :street-type/grove :street-type/harbour :street-type/heath
   :street-type/heights :street-type/highlands :street-type/hill :street-type/hollow
   :street-type/inlet :street-type/island :street-type/key :street-type/knoll :street-type/landing
   :street-type/limits :street-type/link :street-type/lookout :street-type/loop :street-type/mall
   :street-type/manor :street-type/maze :street-type/meadow :street-type/mews :street-type/moor :street-type/mount
   :street-type/mountain :street-type/orchard :street-type/parade :street-type/park :street-type/parkway
   :street-type/passage :street-type/path :street-type/pathway :street-type/pines :street-type/place
   :street-type/plateau :street-type/plaza :street-type/point :street-type/port :street-type/private
   :street-type/promenade :street-type/quay :street-type/ramp :street-type/range :street-type/ridge :street-type/rise
   :street-type/route :street-type/row :street-type/run :street-type/square
   :street-type/subdivision :street-type/terrace :street-type/thicket :street-type/towers
   :street-type/trail :street-type/turnabout :street-type/vale :street-type/view :street-type/village
   :street-type/villas :street-type/vista :street-type/walk :street-type/wharf :street-type/wood :street-type/wynd
   :street-type/diagonal
   :street-type/lake ;; TT-20329 this is what it sounds like - a property accessible by a water body
   :street-type/bourne
   :street-type/summit
   :street-type/isle ;; TT-21016 guess someone decided to be a little archaic
   :street-type/rue ;; TT-21034 why not in French in English
   ])


(def street-types
  (concat common-street-types other-street-types))


(def street-directions
  [:street-direction/E :street-direction/N :street-direction/NE :street-direction/NW
   :street-direction/S :street-direction/SE :street-direction/SW :street-direction/W])


(def unit-designators
  [:unit-designator/apartment :unit-designator/suite :unit-designator/unit :unit-designator/lot])

(def address-states
  [:address.state/active
   :address.state/unverified])

(s/defschema AddressState (apply s/enum address-states))

(defn active-address?
  [{:domain/keys [state]}]
  (= :address.state/active state))

(def address-sources
  [:address-source/user
   :address-source/gis])

(s/defschema AddressSource (apply s/enum address-sources))

(s/defschema AddressSchema
  (st/merge {:address/full-address schemas/NonBlankString}
            (st/optional-keys {:address/street-name          schemas/NonBlankString
                               :address/address-source       AddressSource
                               :domain/state                 AddressState
                               :address/street-number        schemas/NonBlankString
                               :address/street-direction     (apply s/enum street-directions)
                               :address/street-number-suffix s/Str
                               :address/unit-designator      (apply s/enum unit-designators)
                               :address/unit-number          s/Str
                               :address/street-type          (apply s/enum street-types)})))

(def address-checker
  (s/checker AddressSchema))


(def address-keys
  [:address/full-address
   :address/street-name
   :address/address-source
   :address/street-number
   :address/street-direction
   :address/street-number-suffix
   :address/unit-designator
   :address/unit-number
   :address/street-type])


(defn- normalized-full-address [{:address/keys [full-address]}]
  (some-> full-address str/trim str/lower-case))


(defn same-address?
  "Compare address identity case-insensitively, also accepting matching trimmed full addresses."
  [address-a address-b]
  (and (seq (:address/full-address address-a))
       (= (normalized-full-address address-a)
          (normalized-full-address address-b))))


(def address-attributes
  {:CA [:address/street-number
        :address/street-name
        :address/street-type
        :address/street-direction
        :address/unit-designator]
   :US [:address/street-number
        :address/street-direction
        :address/street-name
        :address/street-type
        :address/unit-designator]})


(defn local-address-attributes [subdivision]
  (or (address-attributes subdivision)
      (some-> subdivision
              name
              (str/split #"-")
              first
              keyword
              address-attributes)))


(defmulti format-attribute
  {:arglists '([k address tr use-abbreviation?])}
  (fn [k _ _ _] k))


(defmethod format-attribute :default [k address _ _] (get address k))


(defmethod format-attribute :address/street-number [_ {:address/keys [street-number street-number-suffix]} _ _]
  (when street-number
    (str street-number street-number-suffix)))


(defmethod format-attribute :address/street-type [_ {:address/keys [street-type street-name]} tr use-abbreviation?]
  (when (and (keyword? street-type)
             (not (let [upcase-name (str/upper-case street-name)]
                    (or (str/includes? upcase-name (str/upper-case (tr [street-type])))
                        (str/includes? upcase-name
                                       (str " " (str/upper-case (tr [(keyword "street-type-abbreviation" (name street-type))])) " "))))))
    (str/upper-case (tr [(if use-abbreviation?
                           (keyword "street-type-abbreviation" (name street-type))
                           street-type)
                         (name street-type)]))))


(defmethod format-attribute :address/street-direction [k address tr _]
  (some-> address
          k
          vector
          tr))


(defmethod format-attribute :address/unit-designator [_ {:address/keys [unit-designator unit-number]} tr _]
  (when (keyword? unit-designator)
    (str (tr [(keyword "unit-designator-abbreviation" (name unit-designator))])
         (when unit-number
           (str " " unit-number)))))

#?(:clj
   (defn- format-address [tr subdivision structural-address use-abbreviation?]
     (->> (local-address-attributes subdivision)
          (keep (fn [k]
                  (format-attribute k structural-address tr use-abbreviation?)))
          (str/join " "))))

#?(:clj
   (defn full-address
     "Format a full address string from address components in the format used in the given subdivision / id-domain.
   Returns a translated text indicating address is missing if street name is missing."
     {:deprecated "WIN-2418"}
     ([structural-address subdivision]
      (full-address structural-address subdivision false))
     ([{:address/keys [street-name] :as structural-address} subdivision use-abbreviation?]
      #_{:clj-kondo/ignore [:deprecated-var]}
      (if street-name
        (format-address i18n/tr
                        subdivision
                        structural-address
                        use-abbreviation?)
        (i18n/tr [:address/address-missing])))))
